import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { PackageX, AlertCircle } from 'lucide-react';
import UserLayout from './UserLayout';
import ProductDetailView, { ProductDetailSkeleton } from '../shared/ProductDetailView';
import BOMComponent from '../shared/BOMComponent';
import EmptyState from '../shared/EmptyState';
import Button from '../shared/Button';
import StauffStockModal from '../StauffStockModal';
import AddToInquiryModal from './AddToInquiryModal';
import { showNotification, showError } from '../../utils/notificationHandler';
import { logCustomerSearch, buildShownSnapshot } from '../../utils/searchLogger';
import { notifyCartUpdated } from '../../utils/cartEvents';
import { useOrderingEnabled } from '../../hooks/useOrderingEnabled';

const readCart = () => JSON.parse(localStorage.getItem('cart') || '[]');

const UserProductDetail = () => {
  const { enabled: orderingEnabled, message: orderingMessage } = useOrderingEnabled();
  const { partNumber } = useParams();
  const history = useHistory();
  const location = useLocation();

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  // null | 'notfound' | 'error' — shown in place, so the customer keeps the URL and context
  const [loadProblem, setLoadProblem] = useState(null);
  const [userId, setUserId] = useState(null);
  const [accountId, setAccountId] = useState(localStorage.getItem('accountId'));
  const [canViewFullStock, setCanViewFullStock] = useState(true);
  const [canViewBOMComponents, setCanViewBOMComponents] = useState(true);
  const [account, setAccount] = useState(null);
  const loggedPartRef = useRef(null); // guards against duplicate logs (StrictMode / re-renders)
  const resultPartRef = useRef(null); // part number the current `result` was fetched for

  // Cart & Actions state
  const [cartQuantity, setCartQuantity] = useState(1);
  const [inCartQty, setInCartQty] = useState(0);
  const [isFavourite, setIsFavourite] = useState(false);

  // Stauff modal state
  const [stauffModalOpen, setStauffModalOpen] = useState(false);
  const [stauffModalData, setStauffModalData] = useState(null);
  const [stauffAutoCheck, setStauffAutoCheck] = useState(false);

  // Add to Inquiry state
  const [showAddToInquiryModal, setShowAddToInquiryModal] = useState(false);
  const [inquiryModalData, setInquiryModalData] = useState(null);

  // Check if we came from search results
  const fromSearch = location.state?.fromSearch || sessionStorage.getItem('hasSearchResults') === 'true';

  // The code this part is stored under in the cart and in favourites.
  const productCode = result ? (result.prod_code || result.bom_code) : null;

  const fetchUserData = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setUserId(user.id);
        const { data: accountData, error: accountError } = await supabase
          .from('accounts')
          .select('id, can_view_full_stock, can_view_bom_components, is_admin, is_staff, company_name, full_name')
          .eq('id', user.id)
          .single();

        if (accountError) throw accountError;
        setAccount(accountData);
        setAccountId(accountData.id);
        setCanViewFullStock(accountData.can_view_full_stock);
        setCanViewBOMComponents(accountData.can_view_bom_components);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      showError(error, 'Failed to fetch user data');
    }
  }, []);

  const fetchProductData = useCallback(async () => {
    if (!partNumber || !accountId) return;

    setLoading(true);
    setLoadProblem(null);
    setResult(null);
    try {
      const response = await fetch('/api/enquire/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          partNumber: partNumber,
          account_id: accountId
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.prod_code || data.bom_code) {
        resultPartRef.current = partNumber;
        setResult(data);
      } else {
        setLoadProblem('notfound');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      setLoadProblem('error');
    } finally {
      setLoading(false);
    }
  }, [partNumber, accountId]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  useEffect(() => {
    if (accountId) {
      fetchProductData();
    }
  }, [accountId, fetchProductData]);

  // A new part starts from a quantity of 1.
  useEffect(() => {
    setCartQuantity(1);
  }, [partNumber]);

  // How many of this part are already in the cart.
  useEffect(() => {
    if (!productCode) {
      setInCartQty(0);
      return;
    }
    const line = readCart().find((item) => item.partNumber === productCode);
    setInCartQty(line ? line.quantity : 0);
  }, [productCode]);

  // Whether this part is already a favourite, so the button can be a toggle.
  useEffect(() => {
    if (!userId || !productCode) {
      setIsFavourite(false);
      return undefined;
    }
    let cancelled = false;
    supabase
      .from('favorites')
      .select('part_number')
      .eq('user_id', userId)
      .eq('part_number', productCode)
      .limit(1)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) console.error('Error checking favourite:', error);
        setIsFavourite(!error && (data || []).length > 0);
      });
    return () => {
      cancelled = true;
    };
  }, [userId, productCode]);

  // Log the price/stock shown on this page for Search Analytics. Covers result clicks
  // from fuzzy/description/advanced search and direct links; exact searches from
  // Part Enquiry already logged this view and pass searchLogged.
  useEffect(() => {
    if (!result || !account || !userId) return;
    if (resultPartRef.current !== partNumber) return; // stale result from previous part
    if (location.state?.searchLogged) return;
    if (loggedPartRef.current === partNumber) return;
    loggedPartRef.current = partNumber;

    logCustomerSearch({
      userId,
      account,
      searchTerm: partNumber,
      searchType: 'exact',
      success: true,
      resultsCount: 1,
      partFound: result.prod_code || result.bom_code,
      source: 'product_page',
      shown: buildShownSnapshot(result, account.can_view_full_stock),
    });
  }, [result, account, userId, partNumber, location.state]);

  const handleBackToResults = () => {
    // Check if we have search results in session storage
    const hasResults = sessionStorage.getItem('hasSearchResults') === 'true';
    if (hasResults) {
      // Go back to part enquiry which will restore the results
      history.push('/user/part-enquiry');
    } else {
      // Just go back
      history.goBack();
    }
  };

  const addToCart = (item, quantity = 1) => {
    if (!orderingEnabled) {
      showNotification(orderingMessage, 'error');
      return;
    }
    const code = item.prod_code || item.bom_code;
    const cartItem = {
      partNumber: code,
      description: item.Description || item.bom_description,
      quantity: quantity,
      price: item.buy_price,
      isBOM: !!item.bom_code
    };

    const existingCart = readCart();
    const existingItemIndex = existingCart.findIndex(
      cartItem => cartItem.partNumber === item.prod_code || cartItem.partNumber === item.bom_code
    );

    if (existingItemIndex >= 0) {
      existingCart[existingItemIndex].quantity += quantity;
    } else {
      existingCart.push(cartItem);
    }

    localStorage.setItem('cart', JSON.stringify(existingCart));
    notifyCartUpdated();
    setInCartQty(existingItemIndex >= 0 ? existingCart[existingItemIndex].quantity : quantity);
    showNotification(`${quantity} × ${code} added to cart`, 'success');
  };

  const handleToggleFavourite = async () => {
    if (!userId || !productCode) return;

    if (isFavourite) {
      const { error } = await supabase
        .from('favorites')
        .delete()
        .eq('user_id', userId)
        .eq('part_number', productCode);

      if (error) {
        console.error('Error removing from favorites:', error);
        showError(error, 'Failed to remove from favourites');
      } else {
        setIsFavourite(false);
        showNotification(`Removed "${productCode}" from favourites`, 'success');
      }
      return;
    }

    const { error } = await supabase
      .from('favorites')
      .insert({ user_id: userId, part_number: productCode });

    if (error) {
      console.error('Error adding to favorites:', error);
      showError(error, 'Failed to add to favourites');
    } else {
      setIsFavourite(true);
      showNotification(`Added "${productCode}" to favourites`, 'success');
    }
  };

  const openStauffModal = (partNumber, autoCheck = false, quantity = 1) => {
    setStauffModalData({ partNumber, initialQuantity: quantity });
    setStauffAutoCheck(autoCheck);
    setStauffModalOpen(true);
  };

  const closeStauffModal = () => {
    setStauffModalOpen(false);
    setStauffModalData(null);
    setStauffAutoCheck(false);
  };

  const handleStauffAddToCart = (cartItem) => {
    if (!orderingEnabled) {
      showNotification(orderingMessage, 'error');
      return;
    }
    const existingCart = readCart();
    const existingItemIndex = existingCart.findIndex(
      item => item.partNumber === cartItem.partNumber
    );

    if (existingItemIndex >= 0) {
      existingCart[existingItemIndex].quantity += cartItem.quantity;
    } else {
      existingCart.push(cartItem);
    }

    localStorage.setItem('cart', JSON.stringify(existingCart));
    notifyCartUpdated();
    if (cartItem.partNumber === productCode) {
      setInCartQty(existingItemIndex >= 0 ? existingCart[existingItemIndex].quantity : cartItem.quantity);
    }
  };

  // Handle Add to Inquiry
  const handleAddToInquiry = () => {
    if (!result) return;

    const stockStatus = result.qty_available > 0
      ? `In Stock (${result.qty_available} available)`
      : result.order_status
        ? result.order_status
        : 'Out of Stock';

    setInquiryModalData({
      partNumber: result.prod_code || result.bom_code,
      description: result.Description || result.bom_description,
      price: result.buy_price,
      stockStatus: stockStatus
    });
    setShowAddToInquiryModal(true);
  };

  const handleInquirySuccess = (listId) => {
    setShowAddToInquiryModal(false);
    setInquiryModalData(null);
    // Modal handles navigation prompt internally
  };

  const viewCart = () => history.push('/user/cart');

  const searchSimilar = () =>
    history.push(`/user/part-enquiry?search=${encodeURIComponent(partNumber)}&type=fuzzy`);

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Breadcrumbs — "Search Results" is the one way back to the result list */}
        <nav className="text-sm text-gray-600" aria-label="Breadcrumb">
          <button
            onClick={() => history.push('/user/dashboard')}
            className="hover:text-gray-900 transition-colors duration-200"
          >
            Dashboard
          </button>
          <span className="mx-2" aria-hidden="true">{'>'}</span>
          <button
            onClick={() => history.push('/user/part-enquiry')}
            className="hover:text-gray-900 transition-colors duration-200"
          >
            Part Enquiry
          </button>
          {fromSearch && (
            <>
              <span className="mx-2" aria-hidden="true">{'>'}</span>
              <button
                onClick={handleBackToResults}
                className="hover:text-gray-900 transition-colors duration-200"
              >
                Search Results
              </button>
            </>
          )}
          <span className="mx-2" aria-hidden="true">{'>'}</span>
          <span className="text-gray-900 font-medium font-mono" aria-current="page">{partNumber}</span>
        </nav>

        {/* Product Details */}
        {loading ? (
          <ProductDetailSkeleton />
        ) : result ? (
          result.bom_code && result.components ? (
            <BOMComponent
              bom={result}
              addToCart={addToCart}
              canViewComponents={canViewBOMComponents}
              isFavourite={isFavourite}
              onToggleFavourite={handleToggleFavourite}
              onAddToInquiry={handleAddToInquiry}
              inCartQty={inCartQty}
              onViewCart={viewCart}
            />
          ) : (
            <ProductDetailView
              result={result}
              cartQuantity={cartQuantity}
              setCartQuantity={setCartQuantity}
              onAddToCart={addToCart}
              isFavourite={isFavourite}
              onToggleFavourite={handleToggleFavourite}
              onOpenStauffModal={openStauffModal}
              onAddToInquiry={handleAddToInquiry}
              canViewFullStock={canViewFullStock}
              inCartQty={inCartQty}
              onViewCart={viewCart}
            />
          )
        ) : loadProblem === 'notfound' ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <EmptyState
              icon={<PackageX className="h-12 w-12" />}
              title={`We couldn't find "${partNumber}"`}
              description="Check the part number, or search for similar parts by number or description."
              action={<Button onClick={searchSimilar}>Search similar parts</Button>}
            />
          </div>
        ) : loadProblem === 'error' ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <EmptyState
              icon={<AlertCircle className="h-12 w-12" />}
              title="This part couldn't be loaded"
              description="Something went wrong fetching the price and stock. Try again, or contact us if it keeps happening."
              action={
                <div className="flex flex-wrap justify-center gap-3">
                  <Button onClick={fetchProductData}>Try again</Button>
                  <Button variant="secondary" onClick={() => history.push('/user/part-enquiry')}>
                    Back to Part Enquiry
                  </Button>
                </div>
              }
            />
          </div>
        ) : null}

        {/* One disclaimer for the whole page */}
        {result && (
          <p className="text-xs text-gray-500">
            Images are illustrative and stock levels are indicative only. If in doubt, please contact us before ordering.
          </p>
        )}

        {/* Stauff Stock Modal */}
        <StauffStockModal
          isOpen={stauffModalOpen}
          onClose={closeStauffModal}
          partNumber={stauffModalData?.partNumber}
          autoCheck={stauffAutoCheck}
          onAddToCart={handleStauffAddToCart}
          initialQuantity={stauffModalData?.initialQuantity || 1}
        />

        {/* Add to Inquiry Modal */}
        <AddToInquiryModal
          isOpen={showAddToInquiryModal}
          onClose={() => {
            setShowAddToInquiryModal(false);
            setInquiryModalData(null);
          }}
          productData={inquiryModalData}
          userId={userId}
          onSuccess={handleInquirySuccess}
        />
      </div>
    </UserLayout>
  );
};

export default UserProductDetail;
